import { Link } from 'react-router-dom';

export default function SecurityPage() {
  return (
    <div className="bg-background-50">
      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-4">
            / Trust & Protection
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-foreground-50 leading-tight mb-6">
            Security <span className="text-primary-400">First</span>
          </h1>
          <p className="text-foreground-300 text-base md:text-lg leading-relaxed font-body max-w-2xl mx-auto mb-10">
            Your financial security is our highest priority. Every layer of the TMT platform is
            engineered with bank-grade protection, real-time monitoring, and uncompromising
            compliance standards.
          </p>

          {/* Hero Banner Image */}
          <div className="relative overflow-hidden rounded-2xl border border-primary-700/20">
            <div className="absolute inset-0 bg-gradient-to-t from-background-50/50 via-transparent to-transparent z-10" />
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20cybersecurity%20shield%20protecting%20digital%20financial%20data%20luminous%20golden%20protective%20barrier%20around%20glowing%20data%20nodes%20dark%20sophisticated%20background%20with%20subtle%20geometric%20patterns%20bank%20grade%20security%20visualization&width=1400&height=500&seq=security-hero-8&orientation=landscape"
              alt="TMT Security — Bank-Grade Protection"
              className="w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Security Pillars */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100">
        <div className="max-w-7xl mx-auto">
          {/* Security Section Image */}
          <div className="relative overflow-hidden rounded-2xl mb-12 border border-primary-700/20">
            <div className="absolute inset-0 bg-gradient-to-r from-background-100/60 via-transparent to-transparent z-10" />
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20biometric%20authentication%20interface%20on%20premium%20smartphone%20fingerprint%20scan%20with%20golden%20light%20effect%20elegant%20dark%20background%20cutting%20edge%20fintech%20security%20concept&width=1400&height=450&seq=security-biometric-9&orientation=landscape"
              alt="TMT Biometric Security — Advanced Authentication"
              className="w-full object-cover"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {[
              {
                icon: 'ri-shield-check-line',
                title: '256-Bit Encryption',
                desc: 'Every transaction is protected by military-grade 256-bit AES encryption — the same standard used by the world\'s leading financial institutions. Your data is encrypted at rest and in transit, ensuring complete confidentiality.',
              },
              {
                icon: 'ri-fingerprint-line',
                title: 'Biometric Authentication',
                desc: 'Access your account with fingerprint or facial recognition. Our multi-factor authentication system combines something you know, something you have, and something you are — eliminating unauthorized access.',
              },
              {
                icon: 'ri-government-line',
                title: 'Regulatory Compliance',
                desc: 'TMT operates in full compliance with central banking authorities across the Middle East, Africa, and Europe. We adhere to AML, KYC, and PCI-DSS standards, ensuring your funds are always handled within a regulated framework.',
              },
              {
                icon: 'ri-spy-line',
                title: 'Fraud Prevention',
                desc: 'Our AI-powered fraud detection engine monitors every transaction in real time. Suspicious activity is flagged instantly and blocked before any funds are compromised. Proactive protection, not reactive response.',
              },
              {
                icon: 'ri-server-line',
                title: 'Secure Infrastructure',
                desc: 'TMT runs on redundant, geographically distributed datacenters with 99.9% uptime guarantee. Automatic backups, disaster recovery protocols, and round-the-clock infrastructure monitoring protect your data around the clock.',
              },
              {
                icon: 'ri-eye-off-line',
                title: 'Privacy by Design',
                desc: 'We collect only the data necessary to serve you, and we never sell or share your information with third parties. Your financial privacy is embedded into every product decision we make.',
              },
            ].map((item) => (
              <div
                key={item.title}
                className="p-6 md:p-8 bg-background-50 border border-primary-800/20 rounded-xl hover:border-primary-600/40 transition-all duration-500"
              >
                <div className="w-14 h-14 flex items-center justify-center border border-primary-600/30 rounded-full mb-5">
                  <i className={`${item.icon} text-primary-400 text-2xl`} />
                </div>
                <h3 className="font-heading text-xl text-foreground-100 mb-3">{item.title}</h3>
                <p className="text-foreground-400 text-sm leading-relaxed font-body">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-400 mb-4">
            Your Money. Your Trust. Our Priority.
          </h2>
          <p className="text-foreground-300 text-base leading-relaxed mb-8 font-body">
            Join hundreds of thousands of users who trust TMT with their financial lives.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/signup"
              className="px-8 py-3.5 bg-primary-500 text-background-50 font-label text-sm font-semibold tracking-wider uppercase rounded-full hover:bg-primary-400 transition-colors whitespace-nowrap"
            >
              Open Account
            </Link>
            <Link
              to="/contact"
              className="px-8 py-3.5 border border-primary-600/50 text-primary-400 font-label text-sm tracking-wider uppercase rounded-full hover:bg-primary-500/10 transition-colors whitespace-nowrap"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}